import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRequestTimebankWithdrawal } from "@/hooks/use-timebank";
import { SrIcon } from "@/components/ui/SrIcon";
import { timeDiffMinutes } from "@/lib/format";

interface RequestWithdrawalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentBalance: number;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function RequestWithdrawalDialog({
  open,
  onOpenChange,
  userId,
  currentBalance,
}: RequestWithdrawalDialogProps) {
  const requestMut = useRequestTimebankWithdrawal();
  const [hours, setHours] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  // VIB-1286: klokkeslett er alltid valgfritt (Erik-default, bølge-02-spec).
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setHours("");
      setStartDate(todayIso());
      setEndDate(todayIso());
      setStartTime("");
      setEndTime("");
      setComment("");
    }
  }, [open]);

  const parsedHours = parseFloat(hours);
  const hoursValid = !isNaN(parsedHours) && parsedHours > 0;
  const datesValid =
    !!startDate && !!endDate && new Date(startDate) <= new Date(endDate);
  // VIB-1286: til-tid må være etter fra-tid når begge er oppgitt og gjelder
  // samme dag (ellers er de to ulike dagers uavhengige klokkeslett — ok).
  const timesValid =
    !startTime || !endTime || startDate !== endDate || timeDiffMinutes(startTime, endTime) > 0;
  const overdraft = hoursValid && parsedHours > currentBalance;
  const canSubmit = hoursValid && datesValid && timesValid && !requestMut.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await requestMut.mutateAsync({
        user_id: userId,
        hours: parsedHours,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime || null,
        end_time: endTime || null,
        comment: comment.trim() || undefined,
      });
      onOpenChange(false);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Søk om avspassering
          </DialogTitle>
          <DialogDescription>
            Daglig leder må godkjenne uttaket før timene trekkes fra timebanken.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Antall timer
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm tabular-nums"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="f.eks. 7.5"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Saldo i dag: {currentBalance.toFixed(1)} t
              {overdraft && (
                <span className="ml-1 text-yellow-600">
                  · søknaden overstiger saldo
                </span>
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Fra dato
              </label>
              <input
                type="date"
                className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Til dato
              </label>
              <input
                type="date"
                className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* VIB-1286: klokkeslett — alltid valgfritt, skiller halv dag fra hel dag */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Fra kl. (valgfritt)
              </label>
              <input
                type="time"
                aria-label="Fra klokkeslett"
                className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Til kl. (valgfritt)
              </label>
              <input
                type="time"
                aria-label="Til klokkeslett"
                className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
          {!timesValid && (
            <p className="text-[11px] text-destructive -mt-2">
              Til-tid må være etter fra-tid når begge gjelder samme dag.
            </p>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Kommentar (valgfritt)
            </label>
            <textarea
              rows={2}
              className="mt-1 w-full rounded-md border-[1.5px] border-foreground bg-background px-2 py-1.5 text-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Begrunnelse, plan for fravær, e.l."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={requestMut.isPending}
          >
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {requestMut.isPending ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="send" className="w-4 h-4 mr-1.5" />
            )}
            Send søknad
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
